import { APP_INITIALIZER, ApplicationConfig, ErrorHandler } from '@angular/core';
import { NoPreloading, provideRouter, Router, withComponentInputBinding, withPreloading } from '@angular/router';
import { routes } from './app.routes';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { httpInterceptor } from './shared/interceptors/http.interceptor';
import { provideNativeDateAdapter } from '@angular/material/core';
import { IMAGE_CONFIG } from '@angular/common';
import * as Sentry from "@sentry/angular";
import { ChunkReloadErrorHandler } from './shared/error-handler/chunk-reload-error-handler';
import { environment } from '../environments/environment';

// Import component registry to prevent tree-shaking
import './component-registry';
import { MODAL_CONTENT_COMPONENT } from './shared/modal-content/modal-content.token';
import { ModalContentComponent } from './shared/modal-content/modal-content.component';

// Sentry is only initialized in production (see main.ts) — so register its
// ErrorHandler/TraceService providers only there too. In dev the app uses
// Angular's default ErrorHandler (errors log to the console as expected).
const sentryProviders = environment.production ? [
  {
    // Wraps Sentry's ErrorHandler and adds a one-shot hard reload when a stale
    // post-deploy chunk fails to load (NG0750 / ChunkLoadError / module MIME).
    provide: ErrorHandler,
    useClass: ChunkReloadErrorHandler,
  },
  {
    provide: Sentry.TraceService,
    deps: [Router],
  },
  {
    provide: APP_INITIALIZER,
    useFactory: () => () => { },
    deps: [Sentry.TraceService],
    multi: true,
  },
] : [];

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withComponentInputBinding(), withPreloading(NoPreloading)),
    // Hydration intentionally NOT enabled. The shop shell renders content from
    // data only available asynchronously on the client (products/menu/sidebar/
    // global messages fetched post-bootstrap, plus session-dependent UI), which
    // diverges from the SSR DOM at the synchronous hydration pass and crashes in
    // production with `Cannot set properties of null (setting '__ngContext__')`
    // — fatal on deep categories like /shop/pantry (Angular 20 turns the mismatch
    // fatal; Angular 19 tolerated it). The server still SSRs the full HTML so
    // SEO/LCP/FCP are preserved; the client simply re-renders instead of
    // hydrating. See memory: project_hydration_mismatch_prod_only.
    provideAnimations(),
    provideNativeDateAdapter(),
    {
      provide: IMAGE_CONFIG,
      useValue: {
        disableImageSizeWarning: true,
        disableImageLazyLoadWarning: true,
      }
    },
    provideHttpClient(
      withFetch(),
      withInterceptors([httpInterceptor])
    ),
    ...sentryProviders,
    // Supplies the modal host component to ModalContentService via DI so the
    // service doesn't statically import it — breaks a circular dependency.
    { provide: MODAL_CONTENT_COMPONENT, useValue: ModalContentComponent },
  ]
};
